import { useMemo, useState, useEffect } from "react";
import { DriverStatusPill, TripStatusPill } from "@/components/StatusPills";
import { DRIVER_STATUS_COLORS } from "@/lib/status";
import { MapPin, Phone, Radio, Copy, Check, Rows2, Rows4, Clock, User, Power, PowerOff, CalendarClock } from "lucide-react";
import DriverScheduleDialog from "@/components/DriverScheduleDialog";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return "ahora";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const DENSITY_KEY = "driversPanel.density";

export default function DriversPanel({ drivers, selectedDriverId, onSelect, queuedByDriver = {}, onToggleDriverStatus }) {
  const [density, setDensity] = useState("compact"); // "compact" | "expanded"
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const stored = localStorage.getItem(DENSITY_KEY);
    if (stored === "compact" || stored === "expanded") setDensity(stored);
  }, []);

  const toggleDensity = () => {
    const next = density === "compact" ? "expanded" : "compact";
    setDensity(next);
    localStorage.setItem(DENSITY_KEY, next);
    if (next === "compact") setExpandedIds(new Set());
  };

  const toggleRow = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sorted = useMemo(() => {
    const order = { free: 0, busy: 1, offline: 2 };
    return [...drivers].sort(
      (a, b) => (order[a.driver_status] ?? 3) - (order[b.driver_status] ?? 3)
    );
  }, [drivers]);

  const freeCount = drivers.filter((d) => d.driver_status === "free").length;
  const busyCount = drivers.filter((d) => d.driver_status === "busy").length;
  const globalExpanded = density === "expanded";

  return (
    <div className="w-72 bg-card/85 backdrop-blur-xl border border-border rounded-md overflow-hidden flex flex-col max-h-[calc(100dvh-100px)] md:max-h-[calc(100vh-120px)]">
      <div className="px-3 py-2 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-mono text-primary tracking-widest flex items-center gap-1.5">
            <Radio className="w-3 h-3 pulse-ring rounded-full" /> CONDUCTORES
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleDensity}
              data-testid="drivers-panel-density-toggle"
              title={globalExpanded ? "Vista compacta" : "Vista expandida"}
              className="text-muted-foreground hover:text-white transition-colors"
            >
              {globalExpanded ? <Rows2 className="w-3.5 h-3.5" /> : <Rows4 className="w-3.5 h-3.5" />}
            </button>
            <div className="text-xs text-muted-foreground font-mono">{drivers.length}</div>
          </div>
        </div>
        <div className="flex gap-3 text-[10px] font-mono uppercase tracking-widest">
          <span><span className="text-emerald-400 font-bold">{freeCount}</span> libres</span>
          <span><span className="text-amber-400 font-bold">{busyCount}</span> ocupados</span>
          <span><span className="text-muted-foreground font-bold">{drivers.length - freeCount - busyCount}</span> off</span>
        </div>
      </div>
      {/* Native scroll instead of shadcn ScrollArea so the scrollbar is
          always visible on short screens (dispatcher laptops with 13" or
          split view). The custom scrollbar-* Tailwind arbitrary values
          give a slim, brighter thumb that a dispatcher can grab with the
          mouse or drag with a finger on touch monitors. */}
      <div
        data-testid="drivers-panel-scroll"
        className="flex-1 overflow-y-auto overscroll-contain scroll-smooth [scrollbar-width:thin] [scrollbar-color:hsl(var(--primary)/0.5)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-primary/70"
      >
        <div className="py-1">
          {sorted.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-6">Sin choferes</div>
          )}
          {sorted.map((d) => {
            const isSel = d.id === selectedDriverId;
            const isExpanded = globalExpanded || expandedIds.has(d.id);
            return (
              <DriverRow
                key={d.id}
                driver={d}
                selected={isSel}
                expanded={isExpanded}
                queuedCount={queuedByDriver[d.id] || 0}
                onSelect={onSelect}
                onToggle={() => toggleRow(d.id)}
                onToggleStatus={onToggleDriverStatus}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DriverRow({ driver: d, selected, expanded, queuedCount = 0, onSelect, onToggle, onToggleStatus }) {
  const trip = d.current_trip;
  const canFocus = d.last_lat != null && d.last_lng != null;
  const isOffline = d.driver_status === "offline";
  const isBusy = d.driver_status === "busy";
  // Dispatchers can override the driver's own toggle between LIBRE ↔ OFFLINE.
  // While busy we still show the button but the click asks for confirmation
  // and the backend will 409 (viajes activos) — the toast surfaces the guard.
  const handleStatusToggle = (e) => {
    e.stopPropagation();
    if (!onToggleStatus) return;
    const target = isOffline ? "free" : "offline";
    const label = isOffline ? "poner en LIBRE" : "poner en OFFLINE";
    if (!window.confirm(`¿${label} a ${d.name}?`)) return;
    onToggleStatus(d, target);
  };
  return (
    <div
      className={`border-l-2 transition-colors ${
        selected ? "border-l-primary bg-primary/10" : "border-l-transparent hover:bg-secondary/40"
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect?.(d)}
        onDoubleClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect?.(d); } }}
        data-testid={`driver-card-${d.id}`}
        className="w-full text-left px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary/60"
      >
        {/* Line 1 */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              d.driver_status === "free" ? "glow-green" :
              d.driver_status === "busy" ? "glow-amber" : ""
            }`}
            style={{ background: DRIVER_STATUS_COLORS[d.driver_status] || "#6B7280" }}
          />
          <span className="font-semibold text-[13px] truncate flex-1 leading-tight">{d.name}</span>
          {queuedCount > 0 && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full bg-red-600 text-white text-[10px] font-black leading-none tabular-nums shadow-[0_0_8px_rgba(239,68,68,0.55)]"
              title={`${queuedCount} viaje${queuedCount > 1 ? "s" : ""} en cola`}
              data-testid={`driver-queue-count-${d.id}`}
            >
              {queuedCount} <span className="text-[9px] font-mono uppercase tracking-widest opacity-90">cola</span>
            </span>
          )}
          {d.phone && (
            <>
              <a
                href={`tel:${d.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-white flex-shrink-0"
                data-testid={`driver-phone-tel-${d.id}`}
                title={d.phone}
              >
                <Phone className="w-3 h-3" />
              </a>
              <CopyPhoneBtn phone={d.phone} driverId={d.id} />
            </>
          )}
          <ScheduleViewBtn driver={d} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            data-testid={`driver-expand-${d.id}`}
            title={expanded ? "Contraer" : "Expandir"}
            className="text-muted-foreground/60 hover:text-white flex-shrink-0"
          >
            {expanded ? <Rows2 className="w-3 h-3" /> : <Rows4 className="w-3 h-3" />}
          </button>
          {onToggleStatus && (
            <button
              type="button"
              onClick={handleStatusToggle}
              data-testid={`driver-force-status-${d.id}`}
              disabled={isBusy}
              title={
                isBusy
                  ? "El chofer tiene un viaje activo — libéralo primero"
                  : isOffline
                    ? "Forzar LIBRE (override)"
                    : "Forzar OFFLINE (override)"
              }
              className={`flex-shrink-0 transition-colors ${
                isBusy
                  ? "text-muted-foreground/30 cursor-not-allowed"
                  : isOffline
                    ? "text-emerald-400/70 hover:text-emerald-300"
                    : "text-red-400/70 hover:text-red-300"
              }`}
            >
              {isOffline ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
            </button>
          )}
          <DriverStatusPill status={d.driver_status || "offline"} />
        </div>
        {/* Earnings row — daily + weekly driver_income (only meaningful values shown) */}
        {(Number(d.earnings_today) > 0 || Number(d.earnings_week) > 0) && (
          <div className="flex items-center gap-1 mt-0.5 pl-3.5 text-[10px] font-mono">
            <span
              className="px-1 py-[1px] rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 tabular-nums"
              title="Ganado hoy (Naples EDT)"
              data-testid={`driver-earnings-today-${d.id}`}
            >
              ${Number(d.earnings_today || 0).toFixed(0)} hoy
            </span>
            <span
              className="px-1 py-[1px] rounded bg-sky-500/15 text-sky-300 border border-sky-500/25 tabular-nums"
              title="Ganado en la semana contable actual (viernes → jueves)"
              data-testid={`driver-earnings-week-${d.id}`}
            >
              ${Number(d.earnings_week || 0).toFixed(0)} sem
            </span>
          </div>
        )}
        {/* Line 2 */}
        <div className="flex items-center gap-1.5 mt-0.5 pl-3.5 text-[10px] font-mono min-w-0">
          {trip ? (
            <>
              <TripStatusPill status={trip.status} />
              <span className="truncate text-muted-foreground flex-1">
                {trip.passenger_name || "Pasajero"}
              </span>
              <span className="font-bold text-amber-400 flex-shrink-0">
                ${Number(trip.price || 0).toFixed(0)}
              </span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">hace {timeAgo(d.last_seen)}</span>
              {canFocus && (
                <span className="text-muted-foreground/70 flex items-center gap-0.5 ml-auto">
                  <MapPin className="w-2.5 h-2.5" /> GPS
                </span>
              )}
            </>
          )}
        </div>
        {/* Expanded details */}
        {expanded && (
          <div
            className="mt-1.5 pl-3.5 pr-1 space-y-1 text-[11px]"
            data-testid={`driver-expanded-${d.id}`}
          >
            <div className="flex items-center gap-1.5 text-muted-foreground font-mono">
              <Clock className="w-3 h-3" />
              <span>Última señal: hace {timeAgo(d.last_seen)}</span>
              {canFocus && (
                <span className="ml-auto text-emerald-400/80">
                  <MapPin className="w-2.5 h-2.5 inline" /> GPS activo
                </span>
              )}
            </div>
            {d.phone && (
              <div className="text-muted-foreground font-mono flex items-center gap-1.5">
                <Phone className="w-3 h-3" />
                <span>{d.phone}</span>
              </div>
            )}
            {trip && (
              <div className="pt-1 border-t border-border/60 space-y-1">
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-muted-foreground" />
                  <span className="truncate">{trip.passenger_name || "Pasajero"}</span>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                    #{trip.trip_number || trip.id?.slice(-4)}
                  </span>
                </div>
                <div className="flex items-start gap-1.5 text-muted-foreground">
                  <MapPin className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
                  <span className="truncate">{trip.pickup_address}</span>
                </div>
                <div className="flex items-start gap-1.5 text-muted-foreground">
                  <MapPin className="w-3 h-3 text-accent flex-shrink-0 mt-0.5" />
                  <span className="truncate">{trip.dropoff_address}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleViewBtn({ driver }) {
  const [open, setOpen] = useState(false);
  const hasSchedule = Object.values(driver.schedule?.days || {}).some((b) => b && b.length);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`driver-schedule-view-${driver.id}`}
        title="Ver horario del chofer"
        className={`flex-shrink-0 hover:text-white ${hasSchedule ? "text-sky-400/80" : "text-muted-foreground/60"}`}
      >
        <CalendarClock className="w-3 h-3" />
      </button>
      <DriverScheduleDialog open={open} onOpenChange={setOpen} schedule={driver.schedule} driverName={driver.name} viewer="dispatcher" />
    </>
  );
}

function CopyPhoneBtn({ phone, driverId }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(phone);
      } else {
        const ta = document.createElement("textarea");
        ta.value = phone;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(`Copiado: ${phone}`);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={`copy-phone-${driverId}`}
      title="Copiar teléfono"
      className={`flex-shrink-0 transition-colors ${
        copied ? "text-emerald-400" : "text-muted-foreground hover:text-white"
      }`}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}
